import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { projectsData } from '../../projects/data/projectsData';

const categories = ['すべて', '軽鉄下地LGS工事', 'ボード工事', '壁紙クロス工事', '床仕上工事'];

function ProjectCard({ project: p, index: i }: { project: typeof projectsData[0]; index: number }) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.08 });
  const navigate = useNavigate();

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className="group relative overflow-hidden rounded-sm shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.97)',
        transition: `opacity 0.65s ease ${i * 0.1}s, transform 0.65s ease ${i * 0.1}s`,
      }}
      onClick={() => navigate(`/projects/${p.id}`)}
    >
      <div className="w-full h-56 md:h-64">
        <img
          src={p.image}
          alt={p.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity"></div>

      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="flex items-center gap-2 bg-[#B8960C] text-white text-xs px-4 py-2 tracking-widest">
          <i className="ri-eye-line"></i>
          詳細を見る
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
        <span className="inline-block bg-[#B8960C] text-white text-[10px] md:text-xs px-2 md:px-3 py-1 tracking-wider mb-2">
          {p.category}
        </span>
        <h3
          className="text-white font-bold text-base md:text-lg leading-tight mb-1"
          style={{ fontFamily: "'Noto Serif JP', serif" }}
        >
          {p.title}
        </h3>
        <div className="flex items-center gap-3 md:gap-4 text-white/70 text-[10px] md:text-xs">
          <span className="flex items-center gap-1">
            <i className="ri-map-pin-line"></i>
            {p.location}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {p.year}年竣工
          </span>
        </div>
      </div>
    </div>
  );
}

export default function Projects() {
  const [active, setActive] = useState('すべて');
  const [showAll, setShowAll] = useState(false);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: filterRef, isVisible: filterVisible } = useScrollAnimation({ threshold: 0.1 });

  const filtered = active === 'すべて' ? projectsData : projectsData.filter((p) => p.category === active);
  const displayed = showAll ? filtered : filtered.slice(0, 6);
  const hasMore = filtered.length > 6;

  return (
    <section id="projects" className="py-16 md:py-28 bg-[#F8F6F1]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="text-center mb-10 md:mb-12"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(32px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-px bg-[#B8960C]"></div>
            <span className="text-[#B8960C] text-xs tracking-[0.3em] font-medium uppercase">Our Projects</span>
            <div className="w-8 h-px bg-[#B8960C]"></div>
          </div>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mb-4"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            施工実績
          </h2>
          <p className="text-gray-500 text-sm md:text-base max-w-xl mx-auto leading-relaxed px-4">
            これまでに手がけた代表的なプロジェクトをご紹介します。
          </p>
        </div>

        {/* Filter Tabs */}
        <div
          ref={filterRef as React.RefObject<HTMLDivElement>}
          className="mb-10 md:mb-12 overflow-x-auto scrollbar-hide"
          style={{
            opacity: filterVisible ? 1 : 0,
            transform: filterVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s ease 0.15s, transform 0.6s ease 0.15s',
          }}
        >
          <div className="flex items-center justify-start md:justify-center gap-2 min-w-max px-4 md:px-0">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => { setActive(cat); setShowAll(false); }}
                className={`px-4 md:px-5 py-2 text-xs md:text-sm font-medium tracking-wider transition-all cursor-pointer whitespace-nowrap ${
                  active === cat
                    ? 'bg-[#B8960C] text-white'
                    : 'bg-white text-gray-500 border border-gray-200 hover:border-[#B8960C] hover:text-[#B8960C]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {displayed.map((p, i) => (
            <ProjectCard key={p.id} project={p} index={i} />
          ))}
        </div>

        {/* もっと見る / 閉じる Button */}
        {hasMore && (
          <div className="flex justify-center mt-10 md:mt-12">
            <button
              onClick={() => setShowAll(!showAll)}
              className="flex items-center gap-2 px-8 md:px-10 py-3 border border-[#B8960C] text-[#B8960C] text-xs md:text-sm font-medium tracking-widest hover:bg-[#B8960C] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              {showAll ? (
                <>
                  <i className="ri-arrow-up-s-line text-base"></i>
                  閉じる
                </>
              ) : (
                <>
                  もっと見る
                  <span className="text-xs opacity-70">（{filtered.length - 6}件）</span>
                  <i className="ri-arrow-down-s-line text-base"></i>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}